'use client';

import { use, useEffect, useState } from 'react';

import Icon from './Icon';
import Img from './Image';
import Modal from './Modal';

type Picture = {
  url: string;
  alt?: string;
  name?: string;
};

type ThumbnailPosition = 'top' | 'bottom' | 'left' | 'right';

type ProductGalleryProps = {
  gallery?: Picture[];
  sizeGuides?: Picture[];
  thumbnailPosition?: ThumbnailPosition;
};

const ProductGalleryView = ({ gallery = [], sizeGuides = [], thumbnailPosition = 'right' }: ProductGalleryProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const allImages = [...gallery, ...sizeGuides];

  const nextImage = () => {
    setSelectedIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setSelectedIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  const closeFullscreen = () => {
    setIsFullscreen(false);
  };

  const handleThumbnailClick = (index: number) => {
    setSelectedIndex(index);
  };

  const handleMainImageClick = () => {
    setIsFullscreen(true);
  };

  if (allImages.length === 0) {
    return null;
  }

  const selectedImage = allImages[selectedIndex];

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isFullscreen) return;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          prevImage();
          break;
        case 'ArrowRight':
          event.preventDefault();
          nextImage();
          break;
        case 'Escape':
          event.preventDefault();
          closeFullscreen();
          break;
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isFullscreen]);

  useEffect(() => {
    console.warn('Gallery updated:', gallery);
  }, [gallery]);

  const renderThumbnails = () => (
    <div
      className={`
            ${thumbnailPosition === 'top' || thumbnailPosition === 'bottom' ? 'flex flex-row gap-2 overflow-x-auto pb-0' : 'flex flex-col gap-2 overflow-y-auto pr-0'}
        `}
    >
      {allImages.map((picture, index) => (
        <button
          key={index}
          onClick={() => handleThumbnailClick(index)}
          className={`
                        shrink-0 border-3 transition-all duration-200 cursor-pointer
                        ${selectedIndex === index ? 'border-primary-300 shadow-md' : 'border-secondary-700 hover:border-secondary-800'}
                        ${thumbnailPosition === 'top' || thumbnailPosition === 'bottom' ? 'w-16 h-auto' : 'w-20 h-auto'}
                    `}
        >
          <Img src={picture.url} alt={picture.alt || `Thumbnail ${index + 1}`} className="w-full h-full object-cover" eager />
        </button>
      ))}
    </div>
  );

  const renderGallery = () => {
    const mainImageClasses = `
            w-full h-full object-contain cursor-zoom-in
            transition-transform duration-600 hover:scale-105
        `;

    const containerClasses = `
            relative overflow-hidden
            ${thumbnailPosition === 'left' || thumbnailPosition === 'right' ? 'flex flex-row' : 'flex flex-col'}
        `;

    const mainContainerClasses = `
            flex-1 relative overflow-hidden
            ${thumbnailPosition === 'left' || thumbnailPosition === 'right' ? 'min-h-auto' : 'min-h-auto'}
        `;

    const thumbnailContainerClasses = `
            ${thumbnailPosition === 'top' ? 'order-first mb-2' : ''}
            ${thumbnailPosition === 'bottom' ? 'order-last mt-2' : ''}
            ${thumbnailPosition === 'left' ? 'order-first mr-2' : ''}
            ${thumbnailPosition === 'right' ? 'order-last ml-2' : ''}
            ${thumbnailPosition === 'top' || thumbnailPosition === 'bottom' ? 'w-full' : 'w-20'}
        `;

    return (
      <div className={containerClasses}>
        {thumbnailPosition === 'top' && <div className={thumbnailContainerClasses}>{renderThumbnails()}</div>}

        {thumbnailPosition === 'left' && <div className={thumbnailContainerClasses}>{renderThumbnails()}</div>}

        <div className={mainContainerClasses}>
          <Img src={selectedImage.url} alt={selectedImage.alt || `Product Image ${selectedIndex + 1}`} className={mainImageClasses} onClick={handleMainImageClick} eager />
        </div>

        {thumbnailPosition === 'right' && <div className={thumbnailContainerClasses}>{renderThumbnails()}</div>}

        {thumbnailPosition === 'bottom' && <div className={thumbnailContainerClasses}>{renderThumbnails()}</div>}
      </div>
    );
  };

  return (
    <>
      <div className="w-full h-full">{renderGallery()}</div>

      <Modal visible={isFullscreen} show={() => setIsFullscreen(true)} close={closeFullscreen} IconClose={<Icon name="close" size={24} />}>
        <div className="relative w-full h-auto min-h-[80vh] flex items-center justify-center">
          {/* Navigation buttons */}
          {allImages.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                className="cursor-pointer absolute left-4 top-1/2 transform -translate-y-1/2 z-10 p-2"
                aria-label="Previous image"
              >
                <Icon name="chevron-left" size={24} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                className="cursor-pointer absolute right-4 top-1/2 transform -translate-y-1/2 z-10 p-2"
                aria-label="Next image"
              >
                <Icon name="chevron-right" size={24} />
              </button>
            </>
          )}

          {/* Main image */}
          <div className="relative max-w-full max-h-full">
            <Img src={selectedImage?.url} alt={selectedImage?.alt || `Product Image ${selectedIndex + 1}`} className="object-contain" eager />
          </div>

          {/* Image counter */}
          {allImages.length > 1 && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-opacity-70 px-4 py-2 rounded-full text-sm font-bold">
              {selectedIndex + 1} / {allImages.length}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default ProductGalleryView;
